import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { ModelFacts } from '@vrlearning/lesson-schema';
import { contactShadow, type ContactShadow } from './contact-shadow.js';

/**
 * A library model's GLB in a lesson (ADR-043; spec "Engine behaviour",
 * "Loading"): fetched by its signed URL, parsed with the plain GLTFLoader --
 * no Draco, meshopt or KTX2: the headset's CSP allows no WebAssembly, and the
 * worker writes plain glTF -- and its textures read through `TextureLoader`
 * (an `<img>`), as the dino spike found: GLTFLoader's ImageBitmapLoader
 * `fetch()`es the GLB's `blob:` images, which the CSP's connect-src refuses.
 *
 * The served GLB is scaled to real size and centred on the ground but not
 * turned: `fitModel` turns it by −`forwardYaw` into the model frame (forward
 * −z) and scales it by the lesson's `size`. A skinned mesh is never culled (its
 * bounds are its bind pose: a walking T. rex would vanish at the view's
 * edge). Beyond the 6.5 m the one soft shadow covers, a blob under its feet
 * grounds it instead.
 */

/** The shadow map covers the stage out to here (look.ts); beyond, a blob. */
export const SHADOW_REACH = 6.5;

export type LoadedModel = {
  readonly scene: THREE.Object3D;
  readonly animations: readonly THREE.AnimationClip[];
};

/** A GLTFLoader that reads a GLB's images through `<img>`, as the CSP allows. */
export function modelLoader(): GLTFLoader {
  const loader = new GLTFLoader();
  loader.register((parser) => {
    parser.textureLoader = new THREE.TextureLoader(parser.options.manager);
    parser.textureLoader.setCrossOrigin(parser.options.crossOrigin);
    return { name: 'vrlearning_img_textures' };
  });
  return loader;
}

/** A served GLB's bytes as a scene and its clips. */
export async function parseModel(
  bytes: ArrayBuffer,
  loader: GLTFLoader = modelLoader(),
): Promise<LoadedModel> {
  const gltf = await loader.parseAsync(bytes, '');
  return { scene: gltf.scene, animations: gltf.animations };
}

/** A model fitted into its object: what the runtime moves, plays and listens to. */
export type FittedModel = {
  /** In the model frame (forward −z, on the ground), scaled: the runtime turns it by `turn`. */
  readonly holder: THREE.Group;
  readonly mixer: THREE.AnimationMixer | undefined;
  /** The GLB's clips by their own names. */
  readonly clips: ReadonlyMap<string, THREE.AnimationClip>;
  /** Where its calls come from, when the rig has a head. */
  readonly head: THREE.Object3D | undefined;
  /** Metres above its feet a call comes from without a head bone. */
  readonly mouthHeight: number;
  readonly blob: THREE.Mesh;
  /**
   * Its contact shadow (contact-shadow.ts), shown instead of the blob on a floor that shows no sun
   * shadow (an unlit 360° photo): drawn the first time it is asked for with `build` (so a lit world
   * pays nothing), in the pose the model has then; undefined for a model with nothing low, or not
   * drawn yet.
   */
  contact(build: boolean): ContactShadow | undefined;
  readonly materials: readonly THREE.Material[];
  dispose(): void;
};

/** Quaternius's "Head", a Sketchfab rig's "bn_Head…", never a "…_end" leaf. */
export function findHead(root: THREE.Object3D): THREE.Object3D | undefined {
  let head = root.getObjectByName('Head');
  if (!head) {
    root.traverse((node) => {
      if (!head && /^(bn_)?head(?!.*end)/i.test(node.name)) head = node;
    });
  }
  return head;
}

let blobTexture: THREE.DataTexture | undefined;

/** A soft round shadow, drawn in code (no canvas: tests and workers have none). */
export function blobShadowTexture(): THREE.DataTexture {
  if (blobTexture) return blobTexture;
  const size = 64;
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const r = Math.hypot(x + 0.5 - size / 2, y + 0.5 - size / 2) / (size / 2);
      // 0.55 in the middle, 0.3 at 55 %, nothing at the edge (the spike's gradient).
      const alpha =
        r >= 1 ? 0 : r < 0.55 ? 0.55 - (0.25 * r) / 0.55 : 0.3 * (1 - (r - 0.55) / 0.45);
      data.set([0, 0, 0, Math.round(alpha * 255)], (y * size + x) * 4);
    }
  }
  blobTexture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  blobTexture.needsUpdate = true;
  return blobTexture;
}

/**
 * Fits a loaded GLB into the model frame at the lesson's size: turned by
 * −`forwardYaw`, scaled by `scale` (`modelScale`), casting and catching the
 * one shadow, its skinned meshes never culled, a blob under it (shown by
 * `showBlob` beyond the shadow's reach).
 */
export function fitModel(
  loaded: LoadedModel,
  facts: Pick<ModelFacts, 'box' | 'forwardYaw'>,
  scale: number,
): FittedModel {
  const turned = new THREE.Group();
  turned.rotation.y = (-facts.forwardYaw * Math.PI) / 180;
  turned.add(loaded.scene);
  const holder = new THREE.Group();
  holder.name = 'model';
  holder.scale.setScalar(scale);
  holder.add(turned);

  const materials = new Set<THREE.Material>();
  loaded.scene.traverse((node) => {
    if (!(node instanceof THREE.Mesh)) return;
    node.castShadow = true;
    node.receiveShadow = true;
    if (node instanceof THREE.SkinnedMesh) node.frustumCulled = false;
    for (const material of Array.isArray(node.material) ? node.material : [node.material]) {
      materials.add(material as THREE.Material);
    }
  });

  const blob = new THREE.Mesh(
    new THREE.PlaneGeometry(1, 1),
    new THREE.MeshBasicMaterial({
      map: blobShadowTexture(),
      transparent: true,
      depthWrite: false,
    }),
  );
  blob.name = 'blob';
  blob.rotation.x = -Math.PI / 2;
  blob.scale.set(facts.box.width * 1.5, facts.box.length * 1.15, 1);
  blob.position.y = 0.03;
  blob.renderOrder = 1;
  blob.visible = false;
  holder.add(blob);
  let contact: ContactShadow | undefined;
  let drawn = false;

  const clips = new Map(loaded.animations.map((clip) => [clip.name, clip]));
  const mixer = loaded.animations.length > 0 ? new THREE.AnimationMixer(loaded.scene) : undefined;
  return {
    holder,
    mixer,
    clips,
    head: findHead(loaded.scene),
    mouthHeight: facts.box.height * 0.85,
    blob,
    contact(build) {
      if (build && !drawn) {
        drawn = true;
        contact = contactShadow(loaded.scene, holder, facts.box.height);
        if (contact) holder.add(contact.mesh);
      }
      return contact;
    },
    materials: [...materials],
    dispose(): void {
      mixer?.stopAllAction();
      mixer?.uncacheRoot(loaded.scene);
      blob.geometry.dispose();
      (blob.material as THREE.Material).dispose();
      contact?.dispose();
      loaded.scene.traverse((node) => {
        if (!(node instanceof THREE.Mesh)) return;
        (node.geometry as THREE.BufferGeometry).dispose();
        // A skinned mesh's bone texture, if the GPU made one.
        if (node instanceof THREE.SkinnedMesh) node.skeleton.dispose();
      });
      for (const material of materials) {
        for (const value of Object.values(material)) {
          if (value instanceof THREE.Texture) value.dispose();
        }
        material.dispose();
      }
    },
  };
}

/**
 * What grounds a model at (x, z): on a lit floor the sun's shadow within its reach of the stage
 * centre and the blob beyond; on an `unlit` floor (a 360° photo, which shows no sun shadow) its
 * contact shadow everywhere (the blob when it has none).
 */
export function showBlob(
  fitted: Pick<FittedModel, 'blob' | 'contact'>,
  x: number,
  z: number,
  unlit = false,
): void {
  const contact = fitted.contact(unlit);
  if (contact) contact.mesh.visible = unlit;
  fitted.blob.visible = !(unlit && contact) && (unlit || Math.hypot(x, z) > SHADOW_REACH);
}

/**
 * How seen a model is, 1 to 0, for "Sla over"'s fade: its materials made
 * see-through on the way, and put back as they were once it is whole again.
 */
export function setSeen(fitted: Pick<FittedModel, 'materials'>, seen: number): void {
  for (const material of fitted.materials) {
    const before = material.userData['seen'] as
      { transparent: boolean; opacity: number; depthWrite: boolean } | undefined;
    if (seen >= 1) {
      if (!before) continue;
      material.transparent = before.transparent;
      material.opacity = before.opacity;
      material.depthWrite = before.depthWrite;
      delete material.userData['seen'];
      material.needsUpdate = true;
      continue;
    }
    if (!before) {
      material.userData['seen'] = {
        transparent: material.transparent,
        opacity: material.opacity,
        depthWrite: material.depthWrite,
      };
      material.transparent = true;
      material.depthWrite = false;
      material.needsUpdate = true;
    }
    const kept = (material.userData['seen'] as { opacity: number }).opacity;
    material.opacity = kept * Math.max(0, seen);
  }
}
